import React from "react";
import { Typography } from "@material-ui/core";
import { BENCHMARK_COLOR, fmt } from "./usdsBoxPlotHelpers";

/**
 * The legend drawn above each reach chart.
 *
 * Nivo's own legend only knows the sub groups, so it cannot carry the
 * benchmark. This one always lists the benchmark with its value, whether or
 * not the line is on the plot, and says so when the axis does not reach it.
 * Every chart on the page therefore reads the same way.
 *
 * Items are buttons: clicking one hides that period or the benchmark line,
 * the way chart.js legends behave on the other graph pages.
 *
 * Items of kind "band" and "marker" are a key to the landmarks drawn on the
 * plot (a shaded stretch between two sites, a dashed line where a tributary
 * joins); they are not series, so they are not buttons.
 *
 * @param {array} items [{kind: "box"|"line"|"band"|"marker", color, label, note}]
 * @param {Set<string>} hidden labels currently hidden
 * @param {function} onToggle called with the label clicked
 */
export default function UsdsChartLegend({ items, hidden, onToggle }) {
  if (!items?.length) return null;
  const isHidden = (label) => Boolean(hidden?.has(label));
  const isKey = (item) => item.kind === "band" || item.kind === "marker";
  return (
    <Typography
      component="ul"
      variant="caption"
      aria-label="Legend"
      style={{
        listStyle: "none",
        margin: "0 0 4px 0",
        padding: 0,
        display: "flex",
        flexWrap: "wrap",
        gap: "4px 20px",
        alignItems: "center",
      }}
    >
      {items.map((item) => (
        <li key={`${item.kind}-${item.label}`}>
          <button
            type="button"
            onClick={
              onToggle && !isKey(item) ? () => onToggle(item.label) : undefined
            }
            aria-pressed={!isHidden(item.label)}
            title={
              onToggle ? (isHidden(item.label) ? "Show" : "Hide") : undefined
            }
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "none",
              border: 0,
              padding: 0,
              font: "inherit",
              color: "inherit",
              cursor: onToggle && !isKey(item) ? "pointer" : "default",
              opacity: isHidden(item.label) ? 0.45 : 1,
              textDecoration: isHidden(item.label) ? "line-through" : "none",
            }}
          >
            {item.kind === "band" ? (
              <span
                aria-hidden="true"
                style={{
                  display: "inline-block",
                  width: 18,
                  height: 12,
                  background: item.color,
                  borderLeft: "1px dashed rgba(0,0,0,0.45)",
                  borderRight: "1px dashed rgba(0,0,0,0.45)",
                }}
              />
            ) : item.kind === "marker" ? (
              <svg width="18" height="14" aria-hidden="true">
                <line
                  x1="9"
                  y1="0"
                  x2="9"
                  y2="14"
                  stroke={item.color}
                  strokeWidth="1.5"
                  strokeDasharray="3 3"
                />
              </svg>
            ) : item.kind === "line" ? (
              <svg width="28" height="10" aria-hidden="true">
                <line
                  x1="0"
                  y1="5"
                  x2="28"
                  y2="5"
                  stroke={item.color}
                  strokeWidth="2"
                  strokeDasharray="6 4"
                />
              </svg>
            ) : (
              <span
                aria-hidden="true"
                style={{
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  background: item.color,
                  border: "1px solid rgba(0,0,0,0.35)",
                }}
              />
            )}
            <span>
              {item.label}
              {item.note ? (
                <span style={{ opacity: 0.7 }}> ({item.note})</span>
              ) : null}
            </span>
          </button>
        </li>
      ))}
    </Typography>
  );
}

/**
 * Legend entries for a parameter's benchmark lines.
 * @param {object} parameter the parameter block from the API
 * @param {array} hidden benchmarks the axis does not reach, from computeYDomain
 * @returns {array} legend items of kind "line"
 */
export const benchmarkLegendItems = (parameter, hidden = []) => {
  if (!parameter?.hasBenchmark) return [];
  const unit =
    parameter.unitName && parameter.unitName !== "None"
      ? ` ${parameter.unitName}`
      : "";
  const isHidden = (v) => hidden.some((h) => h.value === v);
  const items = [];
  if (parameter.benchmarkHigh != null) {
    items.push({
      kind: "line",
      color: BENCHMARK_COLOR,
      label: `${parameter.isTwoSided ? "Benchmark (upper)" : "Benchmark"} ${fmt(
        parameter.benchmarkHigh
      )}${unit}`,
      note: isHidden(parameter.benchmarkHigh) ? "above range shown" : null,
    });
  }
  if (parameter.benchmarkLow != null) {
    items.push({
      kind: "line",
      color: BENCHMARK_COLOR,
      label: `${parameter.isTwoSided ? "Benchmark (lower)" : "Benchmark"} ${fmt(
        parameter.benchmarkLow
      )}${unit}`,
      note: isHidden(parameter.benchmarkLow) ? "below range shown" : null,
    });
  }
  return items;
};

const KEY_COLORS = {
  prf: "rgba(46, 125, 50, 0.25)",
  reservoir: "rgba(2, 119, 189, 0.25)",
  confluence: "#6d4c41",
};

/**
 * Key entries for the landmarks a reach actually has, so the shaded stretch
 * and the dashed line are explained on the chart that shows them.
 * @param {array} landmarks landmarksForReach output
 * @returns {array} legend items of kind "band" or "marker"
 */
export const landmarkLegendItems = (landmarks = []) => {
  const kinds = new Set(landmarks.map((lm) => lm.kind));
  const items = [];
  if (kinds.has("prf"))
    items.push({
      kind: "band",
      color: KEY_COLORS.prf,
      label: "Pollutant reduction facility between the two sites",
    });
  if (kinds.has("reservoir"))
    items.push({
      kind: "band",
      color: KEY_COLORS.reservoir,
      label: "Reservoir between inflow and outlet",
    });
  if (kinds.has("confluence"))
    items.push({
      kind: "marker",
      color: KEY_COLORS.confluence,
      label: "Tributary joins here",
    });
  return items;
};
